<template>
    <div>
        <PageHeader title="Colaboradores">
            <template v-slot:icon>
                <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            </template>
        </PageHeader>

        <ActionArea>
            <button class="btn btn-primary" @click="openCreateModal">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Adicionar
            </button>
        </ActionArea>

        <div class="card p-6 mb-6 flex flex-wrap items-end gap-x-6 gap-y-4">
            <div class="flex-1 min-w-48">
                <BaseInput label="Buscar por nome" v-model="search" @keyup="onSearchChange" placeholder="Digite o nome..." />
            </div>
            <div class="flex items-center gap-x-2 pb-1">
                <label class="label mb-0 cursor-pointer select-none flex items-center gap-x-2">
                    <span>Ver inativos</span>
                    <div
                        class="w-10 h-5 rounded-full relative cursor-pointer transition-colors duration-200"
                        :class="showInactives ? 'bg-blue-500' : 'bg-gray-300'"
                        @click="toggleShowInactives"
                    >
                        <span
                            class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                            :class="showInactives ? 'translate-x-5' : 'translate-x-0'"
                        ></span>
                    </div>
                </label>
            </div>
        </div>

        <div class="flex justify-center mb-4">
            <Loading text="Carregando colaboradores..." :show="loading" />
        </div>

        <Alert text="Nenhum colaborador encontrado" :show="!loading && !hierarchyRows.length" />

        <div class="table-container" v-if="!loading && hierarchyRows.length">
            <table class="min-w-full">
                <thead>
                    <tr>
                        <th class="text-left">Nome</th>
                        <th class="text-left">Nível</th>
                        <th class="text-left">Gestor</th>
                        <th class="text-left">Situação</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in hierarchyRows"
                        :key="row.id"
                        class="table-row"
                        :class="{ 'opacity-50': !row.is_active }"
                    >
                        <td>
                            <span
                                class="text-sm font-semibold"
                                :style="row.depth > 0 ? `padding-left: ${row.depth * 20}px` : ''"
                                :class="{
                                    'text-green-600': row.isBoss,
                                    'text-gray-600': row.isSubordinate && !row.isBoss,
                                    'text-gray-800': !row.isSubordinate && !row.isBoss
                                }"
                            >
                                <span v-if="row.isSubordinate" class="text-gray-400 mr-1">↳</span>
                                {{ row.name }}
                            </span>
                        </td>
                        <td>
                            <span class="text-sm text-gray-600">{{ roleLabel(row.role) }}</span>
                        </td>
                        <td>
                            <span class="text-sm font-semibold text-green-600">{{ getBossName(row) }}</span>
                        </td>
                        <td>
                            <Tag v-if="row.is_active" type="success" text="Ativo" />
                            <Tag v-else type="danger" text="Inativo" />
                        </td>
                        <td class="text-right">
                            <div class="flex justify-end gap-x-2">
                                <button class="btn btn-primary-outline btn-sm" @click="openEditModal(row)">Editar</button>
                                <button
                                    class="btn btn-sm"
                                    :class="row.is_active ? 'btn-danger-outline' : 'btn-primary-outline'"
                                    @click="handleToggleActive(row)"
                                >
                                    {{ row.is_active ? 'Desativar' : 'Ativar' }}
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Modal -->
        <div v-if="showModal" class="overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none justify-center items-center flex">
            <div class="relative mx-auto w-full max-w-lg">
                <div class="border-0 rounded-lg shadow-lg flex flex-col bg-white">
                    <div class="flex items-start justify-between p-5 border-b border-solid">
                        <h3 class="page-title">{{ editingWorker ? 'Editar Colaborador' : 'Novo Colaborador' }}</h3>
                        <button class="bg-transparent hover:border-gray-200" @click="closeModal">
                            <span class="text-gray-800 h-6 w-6 text-2xl block">×</span>
                        </button>
                    </div>
                    <div class="p-6 flex flex-col gap-y-4">
                        <div>
                            <BaseInput label="Nome" v-model="form.name" placeholder="Nome completo" required :isInvalid="!!formErrors.name" @input="formErrors.name = ''" />
                            <BaseErrorMessage :message="formErrors.name" />
                        </div>
                        <div>
                            <BaseInput label="E-mail" v-model="form.email" placeholder="email@empresa.com" required :isInvalid="!!formErrors.email" @input="formErrors.email = ''" />
                            <BaseErrorMessage :message="formErrors.email" />
                        </div>
                        <div>
                            <BaseInput label="CPF / Documento" v-model="form.document" placeholder="000.000.000-00" required :isInvalid="!!formErrors.document" @input="formErrors.document = ''" v-maska="MaskService.document" />
                            <BaseErrorMessage :message="formErrors.document" />
                        </div>
                        <BaseSelect
                            label="Nível"
                            v-model="form.role"
                            :options="roleOptions"
                        />
                        <BaseSelect
                            label="Gestor"
                            v-model="form.boss_id"
                            :options="bossOptions"
                            emptyOption="Sem gestor"
                        />
                    </div>
                    <div class="flex items-center justify-end p-6 border-t border-solid gap-x-4">
                        <button class="btn btn-primary-outline" @click="closeModal">Cancelar</button>
                        <button class="btn btn-primary" @click="handleSave" :disabled="saving">
                            {{ saving ? 'Salvando...' : 'Salvar' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showModal" class="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </div>
</template>

<script>
import { ref, computed } from 'vue';
import PageHeader from '@/components/PageHeader';
import ActionArea from '@/components/ActionArea';
import Alert from '@/components/Alert';
import Loading from '@/components/Loading';
import Tag from '@/components/Tag';
import BaseInput from '@/components/forms/BaseInput';
import BaseSelect from '@/components/forms/BaseSelect';
import BaseErrorMessage from '@/components/forms/BaseErrorMessage';
import { WorkerService } from '../services/Entity';
import MaskService from '../services/MaskService';
import Swal from 'sweetalert';

const ROLE_LABELS = {
    owner: 'Proprietário',
    director: 'Diretor',
    manager: 'Gestor',
    worker: 'Colaborador'
};

const ROLE_OPTIONS = [
    { value: 'owner', title: 'Proprietário' },
    { value: 'director', title: 'Diretor' },
    { value: 'manager', title: 'Gestor' },
    { value: 'worker', title: 'Colaborador' },
];

export default {
    components: { PageHeader, ActionArea, Alert, Loading, Tag, BaseInput, BaseSelect, BaseErrorMessage },
    setup() {
        const workers = ref([]);
        const loading = ref(false);
        const saving = ref(false);
        const search = ref('');
        const showInactives = ref(false);
        const showModal = ref(false);
        const editingWorker = ref(null);
        let searchTimeout = null;

        const form = ref({
            name: '',
            email: '',
            document: '',
            role: 'worker',
            boss_id: ''
        });

        const formErrors = ref({ name: '', email: '', document: '' });

        function validateForm() {
            formErrors.value = { name: '', email: '', document: '' };
            let valid = true;

            if (!form.value.name.trim()) {
                formErrors.value.name = 'Nome é obrigatório';
                valid = false;
            }
            if (!form.value.email.trim()) {
                formErrors.value.email = 'E-mail é obrigatório';
                valid = false;
            }
            if (!form.value.document.trim()) {
                formErrors.value.document = 'CPF / Documento é obrigatório';
                valid = false;
            }

            return valid;
        }

        const roleOptions = ref(ROLE_OPTIONS);

        const bossOptions = computed(() =>
            workers.value
                .filter(w => w.is_active)
                .filter(w => !editingWorker.value || w.id !== editingWorker.value.id)
                .map(w => ({ value: w.id, title: w.name }))
        );

        const workerMap = computed(() => {
            const map = {};
            for (const w of workers.value) map[w.id] = w;
            return map;
        });

        const hierarchyRows = computed(() => {
            const rows = [];
            const bossIds = new Set(workers.value.flatMap(w => w.boss || []));
            const subordinateIds = new Set();

            for (const w of workers.value) {
                for (const bossId of (w.boss || [])) {
                    if (workerMap.value[bossId]) subordinateIds.add(w.id);
                }
            }

            function addWithSubordinates(worker, depth) {
                rows.push({
                    ...worker,
                    isSubordinate: depth > 0,
                    isBoss: bossIds.has(worker.id),
                    depth
                });
                const subs = workers.value.filter(s => (s.boss || []).includes(worker.id));
                for (const sub of subs) {
                    addWithSubordinates(sub, depth + 1);
                }
            }

            for (const w of workers.value) {
                if (!subordinateIds.has(w.id)) {
                    addWithSubordinates(w, 0);
                }
            }

            return rows;
        });

        function roleLabel(role) {
            return ROLE_LABELS[role] || role || '—';
        }

        function getBossName(worker) {
            const bossId = (worker.boss || [])[0];
            if (!bossId) return '—';
            return workerMap.value[bossId]?.name || '—';
        }

        async function loadWorkers() {
            loading.value = true;
            try {
                const { data } = await WorkerService.list(showInactives.value, search.value);
                workers.value = data;
            } catch (err) {
                Swal({ title: 'Erro ao carregar colaboradores', icon: 'error' });
            } finally {
                loading.value = false;
            }
        }

        function onSearchChange() {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(loadWorkers, 400);
        }

        function toggleShowInactives() {
            showInactives.value = !showInactives.value;
            loadWorkers();
        }

        function openCreateModal() {
            editingWorker.value = null;
            form.value = { name: '', email: '', document: '', role: 'worker', boss_id: '' };
            showModal.value = true;
        }

        function openEditModal(worker) {
            editingWorker.value = worker;
            form.value = {
                name: worker.name || '',
                email: worker.email || '',
                document: worker.document || '',
                role: worker.role || 'worker',
                boss_id: (worker.boss || [])[0] || ''
            };
            showModal.value = true;
        }

        function closeModal() {
            showModal.value = false;
            editingWorker.value = null;
            formErrors.value = { name: '', email: '', document: '' };
        }

        async function handleSave() {
            if (!validateForm()) return;

            saving.value = true;
            try {
                const payload = {
                    name: form.value.name.trim(),
                    email: form.value.email.trim() || null,
                    document: form.value.document.trim() || null,
                    role: form.value.role,
                    boss_id: form.value.boss_id || null
                };

                if (editingWorker.value) {
                    await WorkerService.update(editingWorker.value.id, payload);
                } else {
                    await WorkerService.create(payload);
                }

                closeModal();
                await loadWorkers();
            } catch (err) {
                Swal({ title: 'Erro ao salvar colaborador', icon: 'error' });
            } finally {
                saving.value = false;
            }
        }

        async function handleToggleActive(worker) {
            const action = worker.is_active ? 'desativar' : 'ativar';
            const confirm = await Swal({
                title: `Deseja ${action} ${worker.name}?`,
                icon: 'warning',
                buttons: ['Cancelar', 'Confirmar']
            });
            if (!confirm) return;

            try {
                await WorkerService.toggleIsActive(worker.id);
                await loadWorkers();
            } catch (err) {
                Swal({ title: `Erro ao ${action} colaborador`, icon: 'error' });
            }
        }

        loadWorkers();

        return {
            workers,
            loading,
            saving,
            search,
            showInactives,
            showModal,
            editingWorker,
            form,
            formErrors,
            roleOptions,
            bossOptions,
            hierarchyRows,
            roleLabel,
            getBossName,
            onSearchChange,
            toggleShowInactives,
            openCreateModal,
            openEditModal,
            closeModal,
            handleSave,
            handleToggleActive,
            MaskService
        };
    }
};
</script>
